import { Fragment, useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { faNum } from "../game/cards";
import { BigMaps, StageStrip } from "../arrows/StageStrip";
import { AlbumEntry } from "../arrows/Album";
import { type ArrowsRoom, ArrowsRoomPicker, arrowsRoomRules, arrowsRoomSummary, loadArrowsRoom, saveArrowsRoom } from "../arrows/RoomPicker";
import { gameArt as art, gameTint as tint, games, roomsOnly, type Game } from "./games";
import { hall, hallName, hasMultiplayer, liveGames, loadAvailability, loadHall, offOnline, offSite } from "./availability";
import { hubShelves } from "./hall";
import { avatar, avatarSrc } from "../game/avatar";
import { CountryFlag } from "./CountryFlag";
import { MenchPiecePicker } from "./MenchPiecePicker";
import { Amount, Level } from "./Wallet";
import { Today } from "./Today";
import { InviteGate, InviteGone, useRegistered } from "./Account";
import { notify } from "./Dialog";
import { guestSeat, roomInvite } from "../net/invite";
import { CupStrip, MatchSearching } from "./CupBits";
import { LobbyAd, playAfterAd } from "./Ads";
import { SCENARIOS } from "../mafia/data";
import { WEB_SCENARIOS, clampSeats, loadMafiaRoom, mafiaRoomRules, saveMafiaRoom, type MafiaRoom } from "../mafia/room";
import { STAKES, loadPrefs, savePrefs } from "./prefs";
import { TablePicker, hasTables, loadTableChoice, saveTableChoice, tableChoiceName, validChoice } from "./TablePicker";
import {
  MENCH_OPT_FA, MENCH_OPT_SUB, MENCH_START_FA, MENCH_START_LABEL, MENCH_START_SUB, MENCH_TEAMS_ROOM_SUB, MENCH_TOGGLES,
  MENCH_TIMED_FA, MENCH_TIMED_LABEL, MENCH_TIMED_SUB,
  loadMenchRules, menchRoomRules, saveMenchRules, type MenchRules, type MenchToggle,
} from "./menchRules";

/**
 * لابی — چیدنِ میز و بازی، و هیچ چیزِ دیگر.
 *
 * هرچه «درباره‌ی من» بود به لندینگ رفت: پشتیبانی، صندوق، اشتراک، خروج از
 * حساب. این‌جا فقط سه تصمیم مانده — کدام بازی، چقدر ورودی، با چه کسی — و
 * همان سه تا **یادشان می‌مانَد**: بازیکنی که هر شب یک جور بازی می‌کند نباید
 * هر شب همان سه انتخاب را از نو بکند.
 *
 * چیدمان — همان «رساندن به میز» ِ صفحه‌ی اولِ اپ:
 *   ۱. کارتِ خودم: چهره، نام، سطح، سکه و گوهر، فروشگاه.
 *   ۲. بازیِ انتخاب‌شده با راه‌های نشستنش — بالای صفحه، تا بازیکنِ هرشبی با یک
 *      لمس سرِ میز برسد و دکمه‌ها زیرِ تای صفحه‌ی گوشی نروند.
 *   ۳. قفسه‌های «ورق» و «تخته‌ای» با کارتِ تصویردار (همان کارتِ تالارِ اپ و سایت).
 *      پیش‌تر یک ردیفِ افقی بود؛ با نُه بازی از صفحه بیرون می‌زد و نام‌ها می‌شکستند.
 */
export function Hub({ onProfile, onCup }: { onProfile: () => void; onCup?: (id: string) => void }) {
  const saved = useState(() => loadPrefs(gameFromUrl()))[0];
  // بازی‌هایی که ادمین خاموش کرده از قفسه می‌روند — [[loadAvailability]].
  const offS = useSignal(offSite);
  const offO = useSignal(offOnline);
  const h = useSignal(hall);
  const live = liveGames(offS, offO).map((x) => hallName(h, x));
  // قفسه‌ها و ترتیبشان از تالارِ پنل؛ «قفسه» همان بازی‌ها به همان ترتیبِ نمایش.
  const shelves = hubShelves(h, live.map((x) => x.id));
  const shelf = shelves.flatMap((s) => s.ids).map((id) => live.find((x) => x.id === id)!);
  useEffect(() => { void loadAvailability(); void loadHall(); }, []);
  const [game, setGame] = useState(saved.game);
  const [stake, setStake] = useState(saved.stake);
  const [open, setOpen] = useState(saved.open);
  const [locked, setLocked] = useState(saved.locked);
  const panel = useRef<HTMLElement>(null);

  // بازیِ انتخاب‌شده خاموش شد؟ به اولین بازیِ قفسه برگرد.
  //
  // بی این، انتخابِ ذخیره‌شده‌ی دیشب روی بازی‌ای می‌مانْد که دیگر روی قفسه
  // نیست: هیچ دکمه‌ای روشن نبود و «بازیِ سریع» میزی می‌خواست که سرور ردش
  // می‌کرد — بی آن‌که بازیکن بفهمد چرا.
  useEffect(() => {
    if (shelf.length === 0) return;
    if (!shelf.some((g) => g.id === game)) setGame(shelf[0]!.id);
  }, [shelf, game]);

  // لینکِ اتاقِ دوست (‎?room=): پنلِ بالا همان بازیِ اتاق می‌شود تا دعوت («masoud تو را به میزِ منچ…» و «بنشین سرِ
  // میز») زیرِ همان بازی بیاید، نه زیرِ حکمِ پیش‌فرض.
  const invite = useSignal(roomInvite);
  useEffect(() => {
    if (invite && invite !== "gone" && shelf.some((x) => x.id === invite.game)) setGame(invite.game);
    // فقط با رسیدنِ پیش‌نمایش؛ قفسه بعدش انتخابِ بازیکن را عوض نمی‌کند
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite]);

  useEffect(() => { savePrefs({ game, stake, open, locked }); }, [game, stake, open, locked]);

  const g = shelf.find((x) => x.id === game) ?? shelf[0];

  // با بازی‌ای آمده (‎?game=، مثلاً «بازی کن» ِ صفحه‌ی اول): فقط قفسه‌ی همان بازی، با کارتِ درشت‌تر —
  // کاربر، ۱ نوامبر ۲۰۲۶: «این صفحه خیلی شلوغ شده؛ وقتی با یه بازی میایم فقط بازی‌های اون دسته رو نشون
  // بده». قفسه همان قفسه‌ی تالارِ پنل است (hubShelves؛ مافیا «دورهمی») و «همه‌ی بازی‌ها» بقیه را
  // برمی‌گرداند. بازی‌ای که دیگر در هیچ قفسه‌ای نیست (خاموش شده)، همان قفسه‌های همیشگی.
  const [only, setOnly] = useState<string | null>(() => gameFromUrl());
  const onlyShelf = only ? shelves.find((s) => s.ids.includes(only)) : undefined;
  // «همه‌ی بازی‌ها» راهِ برگشت دارد (کاربر: «همه‌ی بازی‌ها را برایش برگشت بگذار»): بازی‌ای که با آن آمده بود
  // یادش می‌مانَد و «برگشت» سرِ اولین قفسه همان یک قفسه را برمی‌گرداند.
  const [back, setBack] = useState<string | null>(null);
  const backShelf = back ? shelves.find((s) => s.ids.includes(back)) : undefined;
  const showAll = () => { setBack(only); setOnly(null); setGameInUrl(null); };
  const showBack = () => { if (!back) return; setOnly(back); setBack(null); setGameInUrl(back); };
  const listOf = (ids: string[]) => ids.map((id) => shelf.find((x) => x.id === id)!);

  // انتخاب از قفسه: پنلِ بالا عوض می‌شود و به دید می‌آید — روی گوشی قفسه زیرِ
  // تاست و بی این، بازیکن نمی‌دید چه چیزی عوض شد.
  const pick = (id: string) => {
    setGame(id);
    requestAnimationFrame(() => panel.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }));
  };

  return (
    <div className="page stack hub">
      {/* دو ستون روی دسکتاپ (lux.css، «صفحه‌ی پهن»): این سو خودت و بازیِ انتخاب‌شده با
          دکمه‌هایش، آن سو قفسه‌ها. روی گوشی همان یک ستونِ همیشگی، به همین ترتیب. */}
      <div className="hubside">
        <MeCard onProfile={onProfile} />

        {/* جامِ شبِ جمعه: فقط وقتی جامی در راه یا در جریان است — [[CupStrip]]. */}
        {onCup && <CupStrip onOpen={onCup} />}

        {/* «امروز» بالای قفسه و نه زیرش: چیزی که فقط تا نیمه‌شب هست باید پیش
            از چیزی دیده شود که همیشه هست. */}
        <Today onPlay={(id) => { setGame(id); playAfterAd(() => client.quickMatch(id, 0n), "quick"); }} />

        {/* گردونه‌ی شانس از این‌جا به سربرگ رفت — [[WheelButton]]. */}

        {g && (
          <section className="plate pickpanel" ref={panel} aria-label={`بازی انتخاب‌شده: ${g.fa}`} style={{ "--c": tint(g.color) } as CSSProperties}>
            <GameHead g={g} />
            <Ways g={g} offO={offO} stake={stake} setStake={setStake}
                  open={open} setOpen={setOpen} locked={locked} setLocked={setLocked} />
          </section>
        )}

        {/* بنرِ دوم زیرِ بازیِ انتخاب‌شده — همیشه بعد از دکمه‌ها، هرگز بالای «بازیِ سریع» (web_lobby_slots). */}
        <LobbyAd slot={2} />
      </div>

      <div className="hubmain">
        {onlyShelf ? (
          <GameShelf title={onlyShelf.title} note={onlyShelf.note} list={listOf(onlyShelf.ids)} game={game} onPick={pick} big
                     action={{ label: "همه‌ی بازی‌ها", onClick: showAll }} />
        ) : shelves.map((s, i) => (
          <Fragment key={s.id}>
            {/* بنرِ سوم میانِ قفسه‌ها، پس از اولی؛ در «فقط این قفسه» جایی برایش نیست. */}
            {i === 1 && <LobbyAd slot={3} />}
            <GameShelf title={s.title} note={s.note} list={listOf(s.ids)} game={game} onPick={pick}
                       action={i === 0 && backShelf ? { label: "برگشت", aria: `برگشت به ${backShelf.title}`, onClick: showBack } : undefined} />
          </Fragment>
        ))}

        {/* بنرِ آگهی زیرِ قفسه‌ها، نه بالای «بازیِ سریع» — [[LobbyAd]]. */}
        <LobbyAd />
      </div>
    </div>
  );
}

// ─────────────────────────── کارتِ خودم ───────────────────────────

/**
 * چهره و نام — هر دو در را به پروفایل باز می‌کنند. نام دکمه است و نه جعبه‌ی
 * تایپ: عوض‌کردنش در پروفایل است و سقفِ هفت‌روزه دارد. سطح زیرِ نام و سکه و
 * گوهر کنارش — جایی که سکه دیده می‌شود همان‌جاست که «فروشگاه» به فکر می‌رسد.
 */
function MeCard({ onProfile }: { onProfile: () => void }) {
  const name = useSignal(client.playerName);
  const myAvatar = useSignal(avatar);
  const country = useSignal(client.country);
  const showPlace = useSignal(client.showPlace);
  const wallet = useSignal(client.wallet);
  const delta = useSignal(client.walletDelta);
  return (
    <div className="plate mecard">
      <span className="cflag-host">
        <button className="facebtn" onClick={onProfile} aria-label="پروفایل من" title="پروفایل من">
          <img src={avatarSrc(myAvatar)} alt="" width={48} height={48} />
        </button>
        <CountryFlag code={country} hidden={!showPlace} />
      </span>
      <button className="mename" onClick={onProfile} aria-label="نام و پروفایل من">
        <b>{name || "مهمان"}</b>
        {wallet && wallet.level > 0 && <span className="wallet"><Level state={wallet} /></span>}
      </button>
      {wallet && (
        <div className="mecoins wallet">
          <Amount icon="/art/ui/ic-coin.webp" alt="سکه" value={wallet.coins} delta={delta?.deltaCoins} />
          <Amount icon="/art/ui/ic-gem.webp" alt="گوهر" value={wallet.gems} delta={delta?.deltaGems} />
        </div>
      )}
      {/* فروشگاه صفحه‌ی خودش را دارد (/shop.html): خرید، «میزهای من» و تبدیلِ سکه همه آن‌جاست —
          کاربر، ۲۲ اکتبر ۲۰۲۶. این‌جا فقط بازی و انتخابِ میز. */}
      <a className="hb shopbtn" href="/shop.html" aria-label="فروشگاه" title="فروشگاه">
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 8h14l-1.2 11.1a2 2 0 0 1-2 1.9H8.2a2 2 0 0 1-2-1.9L5 8z" />
          <path d="M9 11V7a3 3 0 0 1 6 0v4" />
        </svg>
        <span>فروشگاه</span>
      </a>
    </div>
  );
}

// ─────────────────────────── بازیِ انتخاب‌شده ───────────────────────────

function GameHead({ g }: { g: Game }) {
  return (
    <div className="pickhead">
      <span className="pickart" style={{ "--c": tint(g.color) } as CSSProperties}>
        <img src={art(g.id)} alt="" width={72} height={72} decoding="async" />
      </span>
      <div className="picktxt">
        <b>{g.fa}</b>
        <span className="pickmeta">
          {g.note}
          <i className="dot online" aria-hidden="true" />
          {g.rooms ? "آنلاین با دوستان" : "آنلاین"}
        </span>
        <span className="muted">{g.blurb}</span>
      </div>
    </div>
  );
}

type WaysProps = {
  g: Game; offO: Set<string>;
  stake: bigint; setStake: (s: bigint) => void;
  open: boolean; setOpen: (f: (v: boolean) => boolean) => void;
  locked: boolean; setLocked: (f: (v: boolean) => boolean) => void;
};

/** قانون‌های اتاقِ مارپله — در همین مرورگر به یاد می‌مانند، برای اتاقِ بعدی. */
type MpRules = { dice: number; magic: number };
const MP_KEY = "bazivan.marpele.rules";
function loadMpRules(): MpRules {
  try {
    const r = JSON.parse(localStorage.getItem(MP_KEY) ?? "") as Partial<MpRules>;
    return { dice: [0, 1, 2].includes(r.dice ?? -1) ? r.dice! : 0, magic: r.magic === 0 ? 0 : 1 };
  } catch { return { dice: 0, magic: 1 }; }
}
function saveMpRules(r: MpRules): void { try { localStorage.setItem(MP_KEY, JSON.stringify(r)); } catch { /* بی‌حافظه هم کار می‌کند */ } }

/** «تاس» و «خانه‌های جادویی» ِ اتاقِ مارپله. */
function MarpeleRules({ rules, set }: { rules: MpRules; set: (r: MpRules) => void }) {
  const dice = [
    { v: 0, fa: "یک تاس", note: "کلاسیک: باید دقیقاً روی ۱۰۰ بایستی." },
    { v: 1, fa: "دو تاس، انتخابی", note: "دو تاس می‌ریزی و خودت یکی را برمی‌داری." },
    { v: 2, fa: "سریع", note: "جمع دو تاس؛ از ۱۰۰ که رد شوی، برمی‌گردی." },
  ];
  return (
    <div className="stack mprules">
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }} role="radiogroup" aria-label="تاس">
        {dice.map((d) => (
          <button key={d.v} role="radio" aria-checked={rules.dice === d.v} className={"chip" + (rules.dice === d.v ? " on" : "")}
                  onClick={() => set({ ...rules, dice: d.v })}>{d.fa}</button>
        ))}
        <button className={"chip" + (rules.magic ? " on" : "")} aria-pressed={!!rules.magic}
                onClick={() => set({ ...rules, magic: rules.magic ? 0 : 1 })}>خانه‌های جادویی</button>
      </div>
      <div className="muted">
        {dice.find((d) => d.v === rules.dice)!.note}{" "}
        {rules.magic ? "خانه‌های چشمک‌زن با هر تاس جا عوض می‌کنند: پادزهر، شمشیر، تاس دوباره، جابه‌جایی با نفر اول، ایست، و ماشین." : "بدون خانه‌ی جادویی."}
      </div>
    </div>
  );
}

/**
 * قانون‌های اتاقِ منچ — همان الگوی مارپله: ردیفِ «شروع» با سه چیپ، و چیپ‌های روشن/خاموش
 * برای شش قانونِ دلخواه، با زیرنویسِ هر روشن. در همین مرورگر می‌مانند (bazivan.mench.rules).
 * «تا نفرِ آخر» و «تیمی» هر دو روشن می‌مانند و همان‌طور می‌روند؛ سرور با شمارِ نشسته‌ها یکی را
 * برمی‌دارد (چهار نفر → تیمی، کمتر → تا نفرِ آخر). زیرنویس همین را می‌گوید.
 */
/**
 * اتاقِ مافیا: سناریو (با نماد و شرحِ کوتاه) و اندازه‌ی میز در بازه‌ی همان سناریو. نقش‌ها را سرور قرعه می‌کشد و
 * هر کس فقط نقشِ خودش را می‌بیند؛ جای خالی ربات است. نمادها: tools/mafia_icons.py ← /art/mafia/scenario/ (تخت، با
 * mask رنگِ متنِ چیپ را می‌گیرند).
 */
function MafiaRoomPicker({ room, set }: { room: MafiaRoom; set: (r: MafiaRoom) => void }) {
  const s = SCENARIOS[room.scenario] ?? SCENARIOS[1]!;
  return (
    <div className="stack mprules mfroom">
      <div className="mfroom-head" id="mfroom-scn">
        <b>سناریو را انتخاب کن</b>
        <span className="muted">هر سناریو نقش‌ها و قاعده‌های خودش را دارد</span>
      </div>
      <div className="row" role="radiogroup" aria-labelledby="mfroom-scn" style={{ gap: 6, flexWrap: "wrap" }}>
        {WEB_SCENARIOS.map((id) => (
          <button key={id} role="radio" aria-checked={room.scenario === id} className={"chip mfscn" + (room.scenario === id ? " on" : "")}
                  onClick={() => set(clampSeats({ ...room, scenario: id }))}>
            <i className="scn-ico" aria-hidden="true" style={{ "--ico": `url(/art/mafia/scenario/${SCENARIOS[id]!.key}.svg)` } as CSSProperties} />
            {SCENARIOS[id]!.fa}
          </button>
        ))}
      </div>
      <div className="mfroom-desc">
        <i className="scn-ico big" aria-hidden="true" style={{ "--ico": `url(/art/mafia/scenario/${s.key}.svg)` } as CSSProperties} />
        <span><b>{s.fa}:</b> {s.desc}</span>
      </div>
      <div className="row mfseats" role="group" aria-label="اندازه‌ی میز">
        <button className="chip" aria-label="یکی کمتر" disabled={room.seats <= s.min} onClick={() => set(clampSeats({ ...room, seats: room.seats - 1 }))}>−</button>
        <b>{faNum(room.seats)} نفر</b>
        <button className="chip" aria-label="یکی بیشتر" disabled={room.seats >= s.max} onClick={() => set(clampSeats({ ...room, seats: room.seats + 1 }))}>+</button>
        <span className="muted">{faNum(s.min)} تا {faNum(s.max)} نفر</span>
      </div>
    </div>
  );
}

function MenchRulesPicker({ rules, set }: { rules: MenchRules; set: (r: MenchRules) => void }) {
  const toggle = (k: MenchToggle) => set({ ...rules, [k]: rules[k] ? 0 : 1 });
  const subs = MENCH_TOGGLES.filter((k) => rules[k] === 1)
    .map((k) => (k === "teams" ? `${MENCH_OPT_SUB.teams} (${MENCH_TEAMS_ROOM_SUB})` : MENCH_OPT_SUB[k]))
    .filter((s) => s);
  const startSub = MENCH_START_SUB[rules.start as 0 | 1 | 2] ?? "";
  return (
    <div className="stack mprules mnrules">
      <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }} role="radiogroup" aria-label={MENCH_START_LABEL}>
        <span className="mnlabel">{MENCH_START_LABEL}</span>
        {MENCH_START_FA.map((label, v) => (
          <button key={v} role="radio" aria-checked={rules.start === v} className={"chip" + (rules.start === v ? " on" : "")}
                  onClick={() => set({ ...rules, start: v })}>{label}</button>
        ))}
      </div>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }} role="group" aria-label="قانون‌های منچ">
        {MENCH_TOGGLES.map((k) => {
          const on = rules[k] === 1;
          return (
            <button key={k} className={"chip" + (on ? " on" : "")} aria-pressed={on}
                    onClick={() => toggle(k)}>{MENCH_OPT_FA[k]}</button>
          );
        })}
      </div>
      <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }} role="radiogroup" aria-label={MENCH_TIMED_LABEL}>
        <span className="mnlabel">{MENCH_TIMED_LABEL}</span>
        {MENCH_TIMED_FA.map((label, v) => (
          <button key={v} role="radio" aria-checked={rules.timed === v} className={"chip" + (rules.timed === v ? " on" : "")}
                  onClick={() => set({ ...rules, timed: v })}>{label}</button>
        ))}
      </div>
      {(startSub || subs.length > 0 || rules.timed > 0) && (
        <div className="muted">{[startSub, ...subs, rules.timed > 0 ? MENCH_TIMED_SUB : ""].filter((s) => s).join(". ")}{"."}</div>
      )}
      {rules.teams === 1 && rules.places === 1 && (
        <div className="muted">با چهار نفر بازی تیمی می‌شود و «تا نفر آخر» ندارد؛ با کمتر، بی‌تیم و تا نفر آخر.</div>
      )}
    </div>
  );
}

/**
 * راه‌های نشستن سرِ میزِ همین بازی.
 *
 * ورق: ورودی بالای هر دو راه است (تصمیمی که پیش از فشردنِ دکمه گرفته می‌شود)،
 * «بازیِ سریع» دکمه‌ی اصلی است و اتاقِ خصوصی و پیوستن با کد دو راهِ دوم‌اند که
 * فقط با لمس باز می‌شوند — هر سه یک‌جا روی صفحه شلوغ بود و «بازیِ سریع» گم می‌شد.
 * تخته‌ای‌ها صف و ورودی ندارند: اتاق راهِ اصلی است.
 */
function Ways({ g, offO, stake, setStake, open, setOpen, locked, setLocked }: WaysProps) {
  const registered = useRegistered();
  const match = useSignal(client.match);
  const wallet = useSignal(client.wallet);
  // لینکِ اتاقِ دوست (‎?room=) برای کسی که حسابِ فعال ندارد: اول دعوت و «شروع» ِ بی‌حساب — [[InviteGate]]
  const invite = useSignal(roomInvite);
  const seated = useSignal(guestSeat);
  const [more, setMore] = useState<"" | "room" | "join">("");
  // «بیشتر» ِ اتاق: قانون‌ها و تماشایی/رمزدار — بسته به دنیا می‌آید، با هر بازی از نو.
  const [opts, setOpts] = useState(false);
  // رمزِ اتاق ذخیره **نمی‌شود**: چیزی که بین دو نفر رد و بدل می‌شود نباید در
  // مرورگر بماند، و اتاقِ بعدی هم رمزِ خودش را می‌خواهد.
  const [pass, setPass] = useState("");
  const [code, setCode] = useState("");
  const [mpRules, setMpRulesState] = useState<MpRules>(loadMpRules);
  const setMpRules = (r: MpRules) => { setMpRulesState(r); saveMpRules(r); };
  const [mfRoom, setMfRoomState] = useState<MafiaRoom>(loadMafiaRoom);
  const setMfRoom = (r: MafiaRoom) => { setMfRoomState(r); saveMafiaRoom(r); };
  const [mnRules, setMnRulesState] = useState<MenchRules>(loadMenchRules);
  const setMnRules = (r: MenchRules) => { setMnRulesState(r); saveMenchRules(r); };
  const [arRoom, setArRoomState] = useState<ArrowsRoom>(loadArrowsRoom);
  const setArRoom = (r: ArrowsRoom) => { setArRoomState(r); saveArrowsRoom(r); };
  // میزِ اتاق: یکی از «میزهای من» ("table:<شناسه>") یا «قرعه» ("") — یادش می‌ماند، مثلِ قانون‌ها
  const [table, setTableState] = useState(loadTableChoice);
  const setTable = (k: string) => { setTableState(k); saveTableChoice(k); };
  // فهرست را همین‌جا می‌گیریم: بی میز، ردیفِ «میز» اصلاً کشیده نمی‌شود (ساختن فقط در فروشگاه است)
  useSignal(client.myTables);
  useEffect(() => { if (registered && client.myTables.value === null) void client.loadMyTables(); }, [registered]);
  useEffect(() => { setMore(""); setOpts(false); }, [g.id]);

  // بازی حساب نمی‌خواهد؛ فقط لینکِ اتاقِ دوست پیش از دکمه‌ها دروازه‌ی خودش را دارد.
  if (!registered && invite && invite !== "gone" && !seated) return <InviteGate room={invite} />;

  // صفِ جام هم همین‌جا دیده می‌شود («بازیِ بعدی در جام» ِ پایانِ میز به لابی برمی‌گردد)،
  // با برچسبِ خودش — [[MatchSearching]].
  if (match?.state === "searching") return <MatchSearching match={match} className="stack ways" />;

  if (!hasMultiplayer(offO, g.id)) {
    // بازی‌ای که ادمین از آنلاین درآورده: نه ورودی، نه صف، نه اتاق —
    // حریفی جز ربات نیست و نشان‌دادنِ آن سه تا فقط وعده‌ی دروغ است.
    return (
      <div className="stack ways">
        <button className="btn" onClick={() => playAfterAd(() => client.soloMatch(g.id), "bots")}>شروع با ربات‌ها</button>
        <div className="muted">این بازی فعلاً چندنفره ندارد؛ میز با ربات‌ها پر می‌شود و ورودی سکه‌ای هم نمی‌خواهد.</div>
      </div>
    );
  }

  const rooms = roomsOnly(g.id);
  const coins = wallet?.coins ?? 0n;
  // ورودیِ سکه‌ای حسابِ فعال می‌خواهد (سرور هم account_required می‌دهد): بی حساب همیشه رایگان،
  // حتی اگر از ورودِ پیشینِ همین مرورگر سطحی به یاد مانده باشد.
  const staked = registered ? stake : 0n;
  const eff = rooms ? 0n : staked;
  const stakeNote = eff > 0n ? ` — ورودی ${faNum(eff)}` : "";
  const gone = !registered && invite === "gone" && <InviteGone />;

  /**
   * ساختنِ اتاق: فقط دکمه پیداست و زیرِ «بیشتر» هرچه تنظیم است — قانون‌های بازی، تماشایی، رمزدار
   * (کاربر، ۲۰ اکتبر ۲۰۲۶: «بخشِ تنظیماتِ بازی باید ساده‌تر باشد»). منچ با سه ردیف چیپ و چند خط
   * توضیح بیش از نیمِ صفحه‌ی گوشی بود و «ساختنِ اتاق» زیرِ تا می‌رفت. بسته هم که باشد، خطِ کنارِ
   * «بیشتر» می‌گوید اتاق با چه قانونی ساخته می‌شود — تنظیمِ پنهان نباید غافلگیر کند.
   *
   * رمز بیرون از «بیشتر» است: رمزدار که روشن ماند (به‌یادمانده از دفعه‌ی پیش)، دکمه بی رمز
   * خاموش است و جعبه‌اش باید دیده شود.
   */
  const roomForm = (primary: boolean) => (
    <div className={"stack roomform" + (primary ? " primary" : "")}>
      <MoreOptions open={opts} onToggle={() => setOpts((v) => !v)}
                   summary={roomSummary(g.id, { mpRules, mnRules, mfRoom, arRoom, open, locked, table: g.id === "mafia" ? "" : validChoice(table) })}>
        {/* مافیا اتاقِ پدرخوانده‌ی خودش را دارد؛ فرشی زیرِ میزش نیست. فقط میزهای ساخته‌شده — ساختن و
            مدیریت در فروشگاه است (کاربر، ۲۲ اکتبر ۲۰۲۶). */}
        {g.id !== "mafia" && hasTables() && (
          <div className="stack" style={{ gap: 6 }}>
            <b className="morelabel">میزهای من</b>
            <TablePicker value={validChoice(table)} onPick={setTable} />
          </div>
        )}
        {g.id === "marpele" && <MarpeleRules rules={mpRules} set={setMpRules} />}
        {g.id === "mench" && <MenchRulesPicker rules={mnRules} set={setMnRules} />}
        {g.id === "mafia" && <MafiaRoomPicker room={mfRoom} set={setMfRoom} />}
        {g.id === "arrows" && <ArrowsRoomPicker room={arRoom} set={setArRoom} />}
        <div className="row" style={{ gap: 8 }} role="group" aria-label="گزینه‌های اتاق">
          {/* توضیح زیرِ چیپ‌ها می‌آید و نه در `title`: روی گوشی تولتیپ دیده نمی‌شود. */}
          <button className={"chip" + (open ? " on" : "")} aria-pressed={open} onClick={() => setOpen((v) => !v)}>تماشایی</button>
          <button className={"chip" + (locked ? " on" : "")} aria-pressed={locked}
                  onClick={() => setLocked((v) => { if (v) setPass(""); return !v; })}>رمزدار</button>
        </div>
        <div className="muted">
          {open ? (g.id === "mafia" ? "بقیه می‌توانند تماشا کنند؛ نقش کسی دیده نمی‌شود." : rooms ? "بقیه می‌توانند تماشا کنند؛ همان تخته را می‌بینند." : "بقیه می‌توانند تماشا کنند؛ دست کسی دیده نمی‌شود.") : "فقط خودتان؛ در فهرست تماشا نمی‌آید."}
          {locked ? " حتی با داشتن کد، بدون رمز نمی‌شود نشست." : ""}
        </div>
      </MoreOptions>
      {locked && (
        <input type="password" value={pass} placeholder="رمز اتاق" aria-label="رمز اتاق" maxLength={64}
               autoComplete="off" onChange={(e) => setPass(e.target.value)} />
      )}
      <button
        className={primary ? "btn bigbtn" : "btn teal"}
        disabled={locked && pass.trim().length === 0}
        // میز: "table:<شناسه>" یکی از «میزهای من» (سرور از فهرستِ میزبان برش می‌دارد)، "" یعنی قرعه سرِ شروع
        // منچ: هر هفت کلید، همیشه — سرور نبودِ کلید را «کلاسیک» می‌خواند
        // مافیا: اندازه‌ی میز از گزینه‌ها (۴ تا ۱۳)، بقیه چهار
        onClick={() => client.createRoom(g.id, g.id === "mafia" ? clampSeats(mfRoom).seats : 4, g.id === "mafia" ? "" : validChoice(table), eff, open, locked ? pass : "",
          g.id === "marpele" ? mpRules : g.id === "mench" ? menchRoomRules(mnRules) : g.id === "mafia" ? mafiaRoomRules(mfRoom)
            : g.id === "arrows" ? arrowsRoomRules(arRoom) : {})}
      >
        ساختن اتاق{stakeNote}
      </button>
    </div>
  );

  const joinForm = (
    <div className="row joinrow">
      <input className="code" inputMode="numeric" maxLength={6} placeholder="کد ۶رقمی اتاق" aria-label="کد اتاق"
             value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
      <button className="btn" disabled={code.length !== 6} onClick={() => { client.joinRoom(code); setCode(""); }}>پیوستن</button>
    </div>
  );

  if (rooms) {
    // فلش‌ها: مرحله‌های آفلاین (بی‌پایان) راهِ اول است و اتاقِ دوستان راهِ دوم — همان چیدمانِ اپ.
    const arrows = g.id === "arrows";
    return (
      <div className="stack ways">
        {gone}
        {arrows && <StageStrip />}
        {arrows && <BigMaps />}
        {arrows && <AlbumEntry />}
        {arrows && <div className="or"><span>یا با دوستانت مسابقه بده</span></div>}
        {/* مافیا هر جای خالی را ربات می‌کند؛ تخته‌ای‌ها فقط ربات‌هایی که میزبان در اتاق می‌نشاند (Room.tsx) و
            دوز و تخته‌نرد دونفره‌اند — میزبانِ تنها یک ربات می‌گیرد (match.boardPlayers) */}
        <div className="muted">
          {g.id === "mafia"
            ? "اتاق بساز و کدش را برای دوستانت بفرست. ورودی ندارد و جای خالی را ربات پر می‌کند."
            : g.id === "dooz" || g.id === "nard"
              ? "اتاق بساز و کدش را برای دوستانت بفرست. ورودی ندارد؛ تنها شروع کنی، یک ربات حریفت می‌شود."
              : "اتاق بساز و کدش را برای دوستانت بفرست. ورودی ندارد؛ کسی نیامد، در اتاق روی صندلی خالی بزن تا ربات بنشیند."}
        </div>
        {(g.id === "mench" || g.id === "marpele") && <MenchPiecePicker />}
        {roomForm(!arrows)}
        <div className="or"><span>یا کد اتاق دوستت را بزن</span></div>
        {joinForm}
      </div>
    );
  }

  return (
    <div className="stack ways">
      {gone}
      <div className="stakes" role="group" aria-label="ورودی میز">
        <span className="muted">ورودی</span>
        {STAKES.map((s) => {
          // سطحِ بسته کم‌رنگ است ولی لمسش خطا می‌دهد، مثلِ اپ (MatchmakingV3) — خطِ توضیحِ همیشگی را کاربر
          // ۲۹ اکتبر ۲۰۲۶ برداشت: «نیاز به توضیح نیست، با انتخابِ ورودی ارور نشان بده».
          const blocked = s > 0n && (!registered ? "ورودی سکه‌ای حساب می‌خواهد؛ اول وارد شو" : s > coins ? "سکه کافی نیست" : "");
          return (
            <button key={String(s)} className={"chip" + (s === staked ? " on" : "") + (blocked ? " locked" : "")}
                    aria-pressed={s === staked} aria-disabled={blocked ? true : undefined}
                    onClick={() => (blocked ? notify(blocked) : setStake(s))}>
              {s === 0n ? "رایگان" : faNum(s)}
            </button>
          );
        })}
      </div>
      {staked > 0n && (
        <div className="muted">
          هر آدم سر میز {faNum(staked)} سکه می‌گذارد و برنده‌ها تقسیم می‌کنند (۱۰٪ برش خانه). ربات نه پول می‌گذارد نه می‌برد. میز دست‌کم دو آدم می‌خواهد، وگرنه رایگان است.
        </div>
      )}
      {/* کارتِ آگهی **پیش از صف**، مثلِ اپ: وقتی سرور میز را ساخت دیگر دیر است.
          اتاقِ دوستان هیچ آگهی‌ای ندارد — شروعِ میزبان همه را با هم می‌کشد. */}
      <button className="btn bigbtn" onClick={() => playAfterAd(() => client.quickMatch(g.id, staked), "quick")}>
        بازی سریع{stakeNote}
      </button>
      <div className="row twoways">
        <button className={"btn ghost" + (more === "room" ? " on" : "")} aria-expanded={more === "room"}
                onClick={() => setMore((m) => (m === "room" ? "" : "room"))}>اتاق خصوصی</button>
        <button className={"btn ghost" + (more === "join" ? " on" : "")} aria-expanded={more === "join"}
                onClick={() => setMore((m) => (m === "join" ? "" : "join"))}>کد اتاق دارم</button>
      </div>
      {more === "room" && roomForm(false)}
      {more === "join" && joinForm}
    </div>
  );
}

/**
 * «بیشتر» — تنظیم‌های اتاق پشتِ یک دکمه. بسته، خطِ کنارش همان تنظیمِ فعلی را خلاصه می‌گوید؛
 * باز، همان چیپ‌های همیشگی زیرش می‌آیند.
 */
function MoreOptions({ open, onToggle, summary, children }: { open: boolean; onToggle: () => void; summary: string; children: ReactNode }) {
  const id = useId();
  return (
    <div className={"moreopts" + (open ? " open" : "")}>
      <button className="moretoggle" aria-expanded={open} aria-controls={id} onClick={onToggle}>
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" />
        </svg>
        <b>بیشتر</b>
        <span className="moresum">{summary}</span>
        <i className="morechev" aria-hidden="true" />
      </button>
      {open && <div className="stack morebody" id={id}>{children}</div>}
    </div>
  );
}

/** خلاصه‌ی یک‌خطیِ تنظیمِ اتاق، برای کنارِ «بیشتر»: قانون‌های روشنِ بازی و تماشایی/رمزدار. */
export function roomSummary(game: string, o: { mpRules: MpRules; mnRules: MenchRules; mfRoom: MafiaRoom; arRoom?: ArrowsRoom; open: boolean; locked: boolean; table?: string }): string {
  const parts: string[] = [];
  if (o.table) parts.push(tableChoiceName(o.table));
  if (game === "mench") {
    parts.push(MENCH_START_FA[o.mnRules.start as 0 | 1 | 2] ?? MENCH_START_FA[1]);
    for (const k of MENCH_TOGGLES) if (o.mnRules[k] === 1) parts.push(MENCH_OPT_FA[k]);
    if (o.mnRules.timed > 0) parts.push(MENCH_TIMED_FA[o.mnRules.timed as 1 | 2]);
  } else if (game === "marpele") {
    parts.push(["یک تاس", "دو تاس", "سریع"][o.mpRules.dice] ?? "یک تاس");
    if (o.mpRules.magic) parts.push("خانه‌های جادویی");
  } else if (game === "arrows") {
    parts.push(arrowsRoomSummary(o.arRoom ?? { pic: 0, size: 0, teams: 0, mode: 0, rounds: 0 }));
  } else if (game === "mafia") {
    const r = clampSeats(o.mfRoom);
    parts.push(`${(SCENARIOS[r.scenario] ?? SCENARIOS[1]!).fa}، ${faNum(r.seats)} نفر`);
  }
  if (o.open) parts.push("تماشایی");
  if (o.locked) parts.push("رمزدار");
  return parts.length ? parts.join(" · ") : "بدون تماشاچی، بدون رمز";
}

// ─────────────────────────── قفسه ───────────────────────────

/**
 * قفسه‌ی یک دسته — کارتِ هر بازی همان زبانِ کارتِ تالارِ اپ و سایت است: رنگِ
 * خودِ بازی، قابِ برنزی، آرت بالا، پلاکِ عاجِ نام پایین.
 *
 * انتخاب یک کادرِ **تو** و نشانِ ✓ است و تمرکز یک حلقه‌ی **بیرون** — پیش‌تر
 * هر دو یک حلقه‌ی طلایی بودند و با Tab انگار بازیِ دیگری انتخاب شده بود.
 */
function GameShelf({ title, note, list, game, onPick, big, action }: {
  title: string; note?: string; list: Game[]; game: string; onPick: (id: string) => void;
  /** قفسه‌ی تنهای «فقط این دسته»: کارتِ درشت‌تر. */
  big?: boolean;
  /** دکمه‌ی کوچکِ تهِ سرِ قفسه: «همه‌ی بازی‌ها» در قفسه‌ی تنها، «برگشت» سرِ اولین قفسه بعد از آن. */
  action?: { label: string; aria?: string; onClick: () => void };
}) {
  if (list.length === 0) return null;
  return (
    <section className={"pickshelf" + (big ? " only" : "")} aria-label={title}>
      <div className="pickshelf-h">
        <b>{title}</b>
        {note && <span className="muted">{note}</span>}
        {action && <button type="button" className="pickshelf-all" aria-label={action.aria} onClick={action.onClick}>{action.label}</button>}
      </div>
      <div className="pickgrid" role="group" aria-label={title} data-n={list.length}>
        {list.map((x) => (
          <button key={x.id} className={"pick" + (x.id === game ? " on" : "")} style={{ "--c": tint(x.color) } as CSSProperties}
                  aria-pressed={x.id === game} aria-label={`${x.fa} — ${x.note}`} onClick={() => onPick(x.id)}>
            <span className="pic"><img src={art(x.id)} alt="" width={96} height={96} loading="lazy" decoding="async" /></span>
            <span className="name">{x.fa}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * بازیِ خواسته‌شده در نشانی.
 *
 * صفحه‌ی هر بازی در سایت به `/play?game=<id>` می‌رود؛ بی این، بازیکن روی
 * بازیِ دفعه‌ی پیش می‌نشست هرچقدر هم که از صفحه‌ی شلم آمده باشد.
 */
function gameFromUrl(): string | null {
  try {
    const q = new URLSearchParams(location.search).get("game");
    if (q && games.some((g) => g.id === q)) return q;
  } catch { /* بی‌نشانی */ }
  return null;
}

/** «همه‌ی بازی‌ها» ‎?game= را از نشانی برمی‌دارد و «برگشت» دوباره می‌گذارد — بارگذاریِ دوباره همان نما را
 *  نشان می‌دهد. replaceState و نه pushState: پیمایشِ برنامه با دکمه‌ی برگشتِ مرورگر به‌هم نمی‌ریزد. */
function setGameInUrl(id: string | null): void {
  try {
    const u = new URL(location.href);
    if (id) u.searchParams.set("game", id); else u.searchParams.delete("game");
    history.replaceState(history.state, "", u.pathname + u.search + u.hash);
  } catch { /* بی‌نشانی */ }
}

